import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActionButtonComponent } from '../action-button/action-button.component';
import { ChipComponent } from '../chip/chip.component';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';

/**
 * Attachment Data Interface
 * Represents a file attachment with metadata
 *
 * Note: uploadedBy and uploadedDate are optional because they may not be
 * available immediately after upload (backend confirm response doesn't include
 * user info) or during case creation (before attachments are persisted).
 */
export interface Attachment {
  readonly id: string;
  readonly fileName: string;
  readonly fileSize: number;
  readonly fileType: string;
  readonly uploadedBy?: string;
  readonly uploadedDate?: Date;
  readonly isAiGenerated: boolean;
}

/**
 * Attachment List Item Component
 *
 * Presentational component for displaying a single attachment with:
 * - File metadata (name, size, date, uploader)
 * - AI badge (if AI-generated)
 * - Download and delete actions
 *
 * @example
 * ```html
 * <cm-attachment-list-item
 *   [attachment]="attachment"
 *   [canDelete]="true"
 *   [deleting]="false"
 *   (download)="onDownload($event)"
 *   (delete)="onDelete($event)"
 * />
 * ```
 */
@Component({
  selector: 'cm-attachment-list-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, ActionButtonComponent, ChipComponent, TooltipDirective],
  templateUrl: './attachment-list-item.component.html',
  styleUrl: './attachment-list-item.component.less',
})
export class AttachmentListItemComponent {
  /**
   * Attachment data to display
   */
  public readonly attachment = input.required<Attachment>();

  /**
   * Whether delete action is allowed
   * @default true
   */
  public readonly canDelete = input<boolean>(true);

  /**
   * Whether this attachment is currently being deleted
   * @default false
   */
  public readonly deleting = input<boolean>(false);

  /**
   * Emitted when user clicks download button
   */
  public readonly download = output<Attachment>();

  /**
   * Emitted when user clicks delete button
   */
  public readonly delete = output<Attachment>();

  /**
   * Formatted file size (B, KB, MB)
   */
  public readonly formattedSize = computed(() => {
    const bytes = this.attachment().fileSize;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  });

  /**
   * Handle download button click
   */
  public onDownloadClick(): void {
    this.download.emit(this.attachment());
  }

  /**
   * Handle delete button click
   */
  public onDeleteClick(): void {
    this.delete.emit(this.attachment());
  }
}
